import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Download, Sparkles, Globe, MessageSquareHeart, Maximize2, ChevronUp, Plus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FullscreenImageViewer } from "@/components/FullscreenImageViewer";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ImageComparisonSlider } from "@/components/ImageComparisonSlider";
import { MobileHeader } from "@/components/mobile/MobileHeader";
import { MobileMenu } from "@/components/mobile/MobileMenu";
import { cn } from "@/lib/utils";
import { getRetentionConfig, SubscriptionTier } from "@/lib/retention";
import { calculateRemainingCredits } from "@/lib/tier-limits";
import { readCachedCredits, writeProfileSnapshot } from "@/lib/profile-snapshot";
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { getPlatform } from '@/lib/platform';
import { downloadImageNative, downloadAllImagesNative, DownloadResult, BatchDownloadResult } from "@/lib/native-download";
import { saveImageToGallery, saveAllImagesToGallery } from "@/lib/native-media-save";
import { applyWatermark, extractBase64FromDataUrl } from "@/lib/watermark";
import { ShareToExploreDialog } from "@/components/ShareToExploreDialog";
import { UpgradeProDialog } from "@/components/UpgradeProDialog";
import type { RoomRefreshOptions } from "@/lib/room-refresh-options";
import { normalizeGenerationMetadata, serializeGenerationMetadata, type SavedGenerationMetadata } from "@/lib/generation-metadata";


interface ResultsState {
  originalImage: string;
  transformedImages: string[];
  roomType: string;
  designStyle: string;
  // Generation params (when starting fresh)
  isGenerating?: boolean;
  customIdeas?: string;
  totalVariations?: number;
  userId?: string;
  subscriptionTier?: SubscriptionTier;
  // Room Refresh generation params.
  mode?: "room_refresh";
  referenceImages?: string[];
  refreshOptions?: RoomRefreshOptions;
  // Loaded-project params (when opening an existing saved design)
  savedDesignId?: string;
  isAlreadyShared?: boolean;
  // Set once generation has begun in this session so a Back navigation
  // never restarts the same run (and consumes credits again).
  generationStarted?: boolean;
  generationRunKey?: string;
}

const MobileResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const resultsData = location.state as ResultsState | null;

  const [currentVariation, setCurrentVariation] = useState(0);
  const [cachedProfile] = useState(readCachedCredits);
  const [credits, setCredits] = useState(cachedProfile?.credits ?? 0);
  const [subscriptionTier, setSubscriptionTier] = useState<SubscriptionTier>(
    (cachedProfile?.tier as SubscriptionTier) ?? "free"
  );
  const [loadingUser, setLoadingUser] = useState(cachedProfile === null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [transformedImages, setTransformedImages] = useState<string[]>([]);
  const [totalVariations, setTotalVariations] = useState(0);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const generationStartedRef = useRef(false);

  // Share to Explore state
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [lastSavedDesignId, setLastSavedDesignId] = useState<string | null>(null);
  const [isShared, setIsShared] = useState(false);

  // Upgrade-to-Pro dialog state (server-side STYLE_REQUIRES_PRO block)
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [lockedStyleName, setLockedStyleName] = useState<string | undefined>(undefined);

  // Redirect if no results data
  useEffect(() => {
    if (!resultsData) {
      toast.error("No results to display");
      navigate("/designer");
      return;
    }

    // If we have pre-loaded images, use them (rewrite URLs through proxy)
    if (resultsData.transformedImages?.length > 0) {
      setTransformedImages(resultsData.transformedImages);
      setTotalVariations(resultsData.transformedImages.length);
      // Hydrate share state from a loaded saved project so the
      // "Share to Explore" button is enabled and reflects current state.
      if (resultsData.savedDesignId) {
        setLastSavedDesignId(resultsData.savedDesignId);
        setIsShared(!!resultsData.isAlreadyShared);
      }
    } else if (resultsData.isGenerating && resultsData.totalVariations) {
      // If we're starting generation, set up state
      setTotalVariations(resultsData.totalVariations);
      setSubscriptionTier(resultsData.subscriptionTier || "free");
    } else {
      toast.error("No results to display");
      navigate("/designer");
    }
  }, [resultsData, navigate]);

  // Load user credits
  useEffect(() => {
    const loadUser = async () => {
      try {
        // Use getSession() instead of getUser() — works offline from cache
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          if (navigator.onLine) {
            navigate("/auth");
          }
          return;
        }

        const currentUser = session.user;

        // Only fetch profile/notifications if online
        if (navigator.onLine) {
          const { data: profile } = await supabase
            .from("profiles")
            .select("designs_this_month, bonus_generations, subscription_tier")
            .eq("id", currentUser.id)
            .single();

          if (profile) {
            setSubscriptionTier(profile.subscription_tier as SubscriptionTier);
            setCredits(calculateRemainingCredits(profile.subscription_tier, profile.designs_this_month, profile.bonus_generations));
            writeProfileSnapshot(currentUser.id, {
              subscription_tier: profile.subscription_tier,
              bonus_generations: profile.bonus_generations ?? 0,
              designs_this_month: profile.designs_this_month ?? 0,
            });
          }


          // Fetch unread notification count
          const { count } = await supabase
            .from("user_notifications")
            .select("*", { count: "exact", head: true })
            .eq("user_id", currentUser.id)
            .eq("is_read", false);

          setUnreadCount(count || 0);
        }
      } catch (error) {
        console.error("Error loading user:", error);
      } finally {
        setLoadingUser(false);
      }
    };

    loadUser();
  }, [navigate]);

  // Auto-save function
  const handleAutoSave = useCallback(async (
    images: string[],
    generationMetadata: SavedGenerationMetadata[] = [],
  ) => {
    if (!resultsData) return;
    
    try {
      const now = new Date();
      const retention = getRetentionConfig(subscriptionTier, true);
      const autoTitle = `Auto-saved Design - ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

      const { data, error } = await supabase.from("saved_designs").insert({
        user_id: resultsData.userId,
        title: autoTitle,
        original_image_url: resultsData.originalImage,
        transformed_image_url: images[0] || null,
        all_variations: images,
        generation_metadata: serializeGenerationMetadata(generationMetadata),
        room_type: resultsData.roomType,
        design_style: resultsData.designStyle,
        custom_prompt: resultsData.customIdeas || null,
        is_auto_saved: true,
        saved_permanently: retention.savedPermanently,
        expires_at: retention.expiresAt,
        retention_policy: retention.retentionPolicy,
      }).select("id").single();

      if (error) throw error;
      if (data?.id) setLastSavedDesignId(data.id);
      toast.success(`✅ Design ${retention.displayText.toLowerCase()}`);
    } catch (error) {
      console.error("Auto-save error:", error);
    }
  }, [resultsData, subscriptionTier]);

  // Keep the history entry in sync with the saved design + share state so that
  // navigating away (e.g. to the feedback page) and pressing Back restores a
  // fully usable results screen with the Share button still enabled.
  useEffect(() => {
    if (isGenerating || !lastSavedDesignId) return;
    const current = (location.state ?? {}) as ResultsState;
    if (
      current.savedDesignId === lastSavedDesignId &&
      !!current.isAlreadyShared === isShared
    ) {
      return;
    }
    try {
      navigate(location.pathname + location.search, {
        replace: true,
        state: {
          ...current,
          isGenerating: false,
          generationStarted: true,
          transformedImages:
            transformedImages.length > 0 ? transformedImages : current.transformedImages,
          savedDesignId: lastSavedDesignId,
          isAlreadyShared: isShared,
        },
      });
    } catch (err) {
      console.warn("Failed to sync results history state:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSavedDesignId, isShared, isGenerating, transformedImages]);


  // Run generation when starting fresh
  useEffect(() => {
    if (
      !resultsData?.isGenerating ||
      !resultsData.userId ||
      generationStartedRef.current ||
      resultsData?.generationStarted
    ) {
      return;
    }

    generationStartedRef.current = true;

    // Mark this run as consumed in the current history entry so Back/forward
    // navigation restores the same results instead of charging credits again.
    const runKey = `gen-run-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    try {
      sessionStorage.setItem(`dc-results-${runKey}`, "started");
    } catch {}
    navigate(location.pathname + location.search, {
      replace: true,
      state: {
        ...resultsData,
        generationStarted: true,
        generationRunKey: runKey,
      },
    });
    
    const runGeneration = async () => {
      setIsGenerating(true);
      const numVariations = resultsData.totalVariations || 3;

      try {
        // Pre-flight UX guard: read current balance and bail early if insufficient.
        const { data: preProfile } = await supabase
          .from("profiles")
          .select("bonus_generations")
          .eq("id", resultsData.userId)
          .single();
        const preBalance = preProfile?.bonus_generations ?? 0;

        if (preBalance < numVariations) {
          toast.error(
            `Not enough credits — need ${numVariations}, have ${preBalance}.`,
            { duration: 6000 }
          );
          setIsGenerating(false);
          return;
        }

        console.log(`Starting parallel generation of ${numVariations} variations (server-authoritative credits)...`);

        const platform = getPlatform();
        const { getAppVersion } = await import("@/lib/platform");
        const appVersion = await getAppVersion();

        // Server (transform-room) is the sole authority for credit consumption
        // and refunds. Client no longer pre-consumes or batch-refunds.
        const successfulImages: string[] = [];
        const generationMetadata: SavedGenerationMetadata[] = [];
        const serverBalances: number[] = [];
        let proBlockShown = false;
        let noImageReturned = false;


        const generationPromises = Array.from({ length: numVariations }, async (_, i) => {
          try {
            const { data, error } = await supabase.functions.invoke("transform-room", {
              body: {
                imageUrl: resultsData.originalImage,
                roomType: resultsData.roomType,
                designStyle: resultsData.designStyle,
                customPrompt: resultsData.customIdeas
                  ? `${resultsData.customIdeas}. Variation ${i + 1}: Create a unique interpretation.`
                  : `Variation ${i + 1}: Create a unique interpretation.`,
                platform,
                appVersion,
                ...(resultsData.mode ? { mode: resultsData.mode } : {}),
                ...(resultsData.referenceImages?.length
                  ? { referenceImages: resultsData.referenceImages }
                  : {}),
                ...(resultsData.refreshOptions
                  ? { refreshOptions: resultsData.refreshOptions }
                  : {}),
              },
            });

            if (typeof data?.credits?.remaining_bonus === 'number') {
              serverBalances.push(data.credits.remaining_bonus);
            }

            if (error) {
              console.error(`Variation ${i + 1} error:`, error);
              return null;
            }

            if (data?.error === 'STYLE_REQUIRES_PRO') {
              console.warn("[transform-room] Pro style blocked by server (mobile)", {
                code: "STYLE_REQUIRES_PRO",
                style: resultsData.designStyle,
                roomType: resultsData.roomType,
                platform,
                appVersion,
                variationIndex: i,
                serverResponse: data,
                timestamp: new Date().toISOString(),
              });
              if (!proBlockShown) {
                proBlockShown = true;
                setLockedStyleName(resultsData.designStyle);
                setUpgradeOpen(true);
                toast.error(
                  "This design style is part of Pro. Pick a free style or upgrade to unlock the full library.",
                  { duration: 6000 }
                );
              }
              return null;
            }

            if (data?.gateway_status === 429 || data?.gateway_status === 402) {
              console.error(`Variation ${i + 1} rate/payment error:`, data.gateway_status);
              return null;
            }

            if (data?.error) {
              if (data.error === 'NO_IMAGE_RETURNED') noImageReturned = true;
              console.error(`Variation ${i + 1} backend error:`, data.error);
              return null;
            }


            if (data?.transformedImage) {
              // Add image immediately for progressive display
              setTransformedImages(prev => [...prev, data.transformedImage]);
              successfulImages.push(data.transformedImage);
              const metadata = normalizeGenerationMetadata(data.transformedImage, data.generation);
              if (metadata) generationMetadata.push(metadata);
              return data.transformedImage;
            }

            return null;
          } catch (err) {
            console.error(`Variation ${i + 1} promise error:`, err);
            return null;
          }
        });

        // Wait for all to complete
        await Promise.allSettled(generationPromises);

        // Sync credit display to the authoritative server balance.
        let finalBalance = preBalance - successfulImages.length;
        if (serverBalances.length > 0) {
          finalBalance = Math.min(...serverBalances);
        }
        const consumed = Math.max(0, preBalance - finalBalance);

        // Refetch profile so the credit chip stays accurate (includes any refunds).
        const { data: freshProfile } = await supabase
          .from("profiles")
          .select("designs_this_month, bonus_generations, subscription_tier")
          .eq("id", resultsData.userId)
          .single();
        if (freshProfile) {
          setCredits(calculateRemainingCredits(
            freshProfile.subscription_tier,
            freshProfile.designs_this_month,
            freshProfile.bonus_generations,
          ));
          finalBalance = freshProfile.bonus_generations;
        }

        // Step 4: Persist the final state into the history entry so Back/forward
        // navigation restores the generated results without restarting generation.
        try {
          navigate(location.pathname + location.search, {
            replace: true,
            state: {
              ...resultsData,
              isGenerating: false,
              generationStarted: true,
              generationRunKey: resultsData?.generationRunKey,
              transformedImages: successfulImages,
            },
          });
        } catch (navErr) {
          console.warn("Failed to replace result state:", navErr);
        }

        // Step 5: Auto-save if we have results
        if (successfulImages.length > 0) {
          await handleAutoSave(successfulImages, generationMetadata);
          toast.success(
            `${successfulImages.length} design${successfulImages.length > 1 ? 's' : ''} ready`,
            {
              description: `${consumed} credit${consumed === 1 ? '' : 's'} used • ${finalBalance} remaining`,
              duration: 4000,
            }
          );
        } else {
          toast.error(
            noImageReturned
              ? "The AI couldn't complete this design. Your credit was returned — try again or pick a different style."
              : "No designs were generated",
            { duration: 6000 }
          );
        }


      } catch (error: any) {
        console.error("Generation error:", error);
        toast.error(error.message || "Failed to generate design");
      } finally {
        setIsGenerating(false);
      }
    };

    runGeneration();
  }, [resultsData, handleAutoSave]);

  // Handle sign out
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const fetchImageAsBase64 = async (imageUrl: string): Promise<string> => {
    const response = await fetch(imageUrl);
    if (!response.ok) throw new Error('Failed to fetch image');

    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]); // strip data:image/...;base64,
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  /**
   * Save and share image on iOS. Accepts either a URL or pre-processed base64 data.
   */
  const saveAndShareImageIOS = async (
    imageUrlOrBase64: string,
    filename: string
  ): Promise<string> => {
    let base64: string;

    // Check if input is already a base64 data URL (watermarked image)
    if (imageUrlOrBase64.startsWith('data:')) {
      base64 = extractBase64FromDataUrl(imageUrlOrBase64);
    } else {
      base64 = await fetchImageAsBase64(imageUrlOrBase64);
    }

    const file = await Filesystem.writeFile({
      path: filename,
      data: base64,
      directory: Directory.Cache,
    });

    return file.uri;
  };

  // Download single image
  const handleDownloadCurrent = async () => {
    if (transformedImages.length === 0) return;

    const originalImageUrl = transformedImages[currentVariation];
    const fileName = `design-${currentVariation + 1}.jpg`;
    const toastId = toast.loading('Saving image...');

    try {
      // Apply watermark for free tier users
      let imageToSave = originalImageUrl;
      if (subscriptionTier === 'free') {
        try {
          imageToSave = await applyWatermark(originalImageUrl);
        } catch (wmError) {
          console.error('Watermark failed, using original:', wmError);
        }
      }

      // ✅ iOS
      if (getPlatform() === 'ios') {
        const uri = await saveAndShareImageIOS(imageToSave, fileName);
        await Share.share({
          title: 'Export Image',
          url: uri,
        });
        toast.success(subscriptionTier === 'free' ? 'Image saved (with watermark)' : 'Image saved to Files!');
        return;
      }

      // ✅ Android native - use Media plugin to save to Gallery (MediaStore API)
      if (getPlatform() === 'android') {
        console.log('Android: Attempting to save to Gallery via Media plugin...');
        const result = await saveImageToGallery(imageToSave, fileName);
        if (result.success) {
          toast.success(subscriptionTier === 'free' 
            ? 'Image saved to Gallery (with watermark)' 
            : 'Image saved to Gallery');
          return;
        }
        // Fallback to Share sheet if Media plugin fails
        console.warn('Media plugin failed, falling back to Share sheet:', result.error);
        toast.info(`Gallery save issue: ${result.error}. Opening share sheet...`);
        const nativeResult: DownloadResult = await downloadImageNative(imageToSave, fileName);
        if (nativeResult.success) {
          toast.success(subscriptionTier === 'free' 
            ? 'Choose where to save (with watermark)' 
            : 'Choose where to save your image');
          return;
        }
      }

      // 🌐 Web fallback
      let blobUrl: string;
      if (imageToSave.startsWith('data:')) {
        blobUrl = imageToSave; // Use data URL directly
      } else {
        const response = await fetch(imageToSave);
        const blob = await response.blob();
        blobUrl = URL.createObjectURL(blob);
      }

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        if (!imageToSave.startsWith('data:')) {
          URL.revokeObjectURL(blobUrl);
        }
      }, 100);

      toast.success(subscriptionTier === 'free' ? 'Image downloaded (with watermark)' : 'Image downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save image');
    } finally {
      toast.dismiss(toastId);
    }
  };


  const handleDownloadAll = async () => {
    if (transformedImages.length === 0) return;

    const platform = getPlatform();
    const toastId = toast.loading(`Saving ${transformedImages.length} images...`);
    const isFree = subscriptionTier === 'free';

    try {
      // Pre-process images with watermark for free tier
      let imagesToSave = transformedImages;
      if (isFree) {
        const watermarkedImages: string[] = [];
        for (const imgUrl of transformedImages) {
          try {
            const watermarked = await applyWatermark(imgUrl);
            watermarkedImages.push(watermarked);
          } catch (wmError) {
            console.error('Watermark failed for one image:', wmError);
            watermarkedImages.push(imgUrl); // Fallback to original
          }
        }
        imagesToSave = watermarkedImages;
      }

      // ✅ iOS
      if (platform === 'ios') {
        const uris: string[] = [];

        for (let i = 0; i < imagesToSave.length; i++) {
          const imageData = imagesToSave[i];
          let base64: string;

          if (imageData.startsWith('data:')) {
            base64 = extractBase64FromDataUrl(imageData);
          } else {
            base64 = await fetchImageAsBase64(imageData);
          }

          const file = await Filesystem.writeFile({
            path: `design-${i + 1}.jpg`,
            data: base64,
            directory: Directory.Cache,
          });
          uris.push(file.uri);
        }

        await Share.share({
          title: 'Export Designs',
          files: uris,
        });

        toast.success(isFree ? 'Images exported (with watermark)' : 'Images exported');
        return;
      }

      // ✅ Android native - use Media plugin to save to Gallery (MediaStore API)
      if (platform === 'android') {
        console.log('Android: Attempting to save all images to Gallery via Media plugin...');
        const result = await saveAllImagesToGallery(imagesToSave, 'design');
        if (result.successCount > 0) {
          toast.success(isFree 
            ? `${result.successCount} images saved to Gallery (with watermark)` 
            : `${result.successCount} images saved to Gallery`);
          return;
        }
        // Fallback to Share sheet if Media plugin fails
        console.warn('Media plugin failed for all images, falling back to Share sheet:', result.error);
        toast.info(`Gallery save issue: ${result.error}. Opening share sheet...`);
        const nativeResult: BatchDownloadResult = await downloadAllImagesNative(imagesToSave, 'design');
        if (nativeResult.count > 0) {
          toast.success(isFree 
            ? `Choose where to save ${nativeResult.count} images (with watermark)` 
            : `Choose where to save ${nativeResult.count} images`);
          return;
        }
      }

      // 🌐 Web fallback
      let successCount = 0;

      for (let i = 0; i < imagesToSave.length; i++) {
        const imageData = imagesToSave[i];
        let blobUrl: string;

        if (imageData.startsWith('data:')) {
          blobUrl = imageData;
        } else {
          const response = await fetch(imageData);
          if (!response.ok) continue;
          const blob = await response.blob();
          blobUrl = URL.createObjectURL(blob);
        }

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `design-${i + 1}.jpg`;
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
          if (!imageData.startsWith('data:')) {
            URL.revokeObjectURL(blobUrl);
          }
        }, 100);

        successCount++;
        await new Promise(r => setTimeout(r, 300));
      }

      toast.success(isFree ? `Downloaded ${successCount} images (with watermark)` : `Downloaded ${successCount} images`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to export images');
    } finally {
      toast.dismiss(toastId);
    }
  };


  if (!resultsData) {
    return null;
  }

  const { originalImage } = resultsData;
  const progressPercent = totalVariations > 0 ? (transformedImages.length / totalVariations) * 100 : 0;
  const hasImages = transformedImages.length > 0;

  return (
    <div className="page-stable bg-[#FFF7F1] pb-28">
      {/* Header */}
      <MobileHeader
        credits={credits}
        loading={loadingUser}
        onMenuClick={() => setMenuOpen(true)}
        onNotificationClick={() => navigate("/notifications")}
        onExploreClick={() => navigate("/explore")}
        onBlogClick={() => navigate("/blog")}
        unreadCount={unreadCount}
      />

      {/* Menu */}
      <MobileMenu
        open={menuOpen}
        onOpenChange={setMenuOpen}
        subscriptionTier={subscriptionTier}
        onSignOut={handleSignOut}
      />

      {/* Content */}
      <div className="pt-safe-header px-4 pb-6 space-y-6">
        {/* Title */}
        <div className="flex items-center gap-2 mt-4">
          <h1 className="text-xl font-bold text-primary">
            Your Transformed Designs
          </h1>
          <Sparkles className="h-5 w-5 text-primary" />
        </div>

        {/* Progress Bar - shown during generation */}
        {isGenerating && (
          <div className="space-y-2 bg-white rounded-xl p-4 shadow-sm">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-foreground">
                Generating Design Variations
              </span>
              <span className="text-sm text-muted-foreground">
                {transformedImages.length} / {totalVariations}
              </span>
            </div>
            <Progress value={progressPercent} className="h-2" />
            <p className="text-xs text-muted-foreground text-center">
              Each variation appears as it completes
            </p>
          </div>
        )}

        {/* Comparison Slider - shown when we have at least one image */}
        {hasImages && (
          <>
            <div className="w-full relative">
              <ImageComparisonSlider
                beforeImage={originalImage}
                afterImage={transformedImages[currentVariation] || transformedImages[0]}
                heightClassName="h-[52vh] max-h-[560px] min-h-[340px]"
              />
              <Button
                variant="secondary"
                size="icon"
                aria-label="View fullscreen"
                onClick={() => setFullscreenOpen(true)}
                className="absolute bottom-3 right-3 z-10 h-10 w-10 rounded-full bg-black/70 backdrop-blur hover:bg-black/80 text-white shadow-md"
              >
                <Maximize2 className="h-4 w-4 text-white" />
              </Button>
            </div>

            {/* Variation Selector + Start Design */}
            <div className="flex items-start justify-between gap-2 py-1">
              <div className="flex flex-wrap items-center gap-2">
                {transformedImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentVariation(index)}
                    className={cn(
                      "h-10 w-10 rounded-lg font-semibold text-sm transition-all",
                      currentVariation === index
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    )}
                  >
                    {index + 1}
                  </button>
                ))}
                {isGenerating && Array.from({ length: totalVariations - transformedImages.length }).map((_, i) => (
                  <div
                    key={`placeholder-${i}`}
                    className="h-10 w-10 rounded-lg bg-muted/50 animate-pulse"
                  />
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/designer")}
                disabled={isGenerating}
                className="h-10 gap-1 border-2 border-black text-black hover:bg-black/5 hover:text-black disabled:opacity-50 shrink-0"
              >
                <Plus className="h-4 w-4 text-black" />
                <span className="text-sm font-medium">Start Design</span>
              </Button>
            </div>
          </>
        )}

        {/* Loading state when no images yet */}
        {!hasImages && isGenerating && (
          <div className="flex flex-col items-center justify-center py-16 space-y-4">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-8 w-8 text-primary animate-pulse" />
            </div>
            <p className="text-sm text-muted-foreground">
              Generating your first design...
            </p>
          </div>
        )}
      </div>

      {/* Simple AI-generated label at bottom of screen */}
      {hasImages && (
        <div className="bottom-above-dock fixed left-0 right-0 z-20 flex justify-center pointer-events-none">
          <p className="text-xs text-muted-foreground">AI-generated</p>
        </div>
      )}

      {/* Sticky Bottom Action Dock */}
      {hasImages && (
        <div
          className="fixed bottom-0 inset-x-0 z-30 bg-[#FFF7F1]/95 backdrop-blur border-t border-border safe-area-inset-bottom shadow-[0_-4px_12px_-6px_rgba(0,0,0,0.15)]"
        >
          <div className="flex items-stretch gap-2 px-3 py-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  disabled={!hasImages}
                  aria-label={getPlatform() === "ios" ? "Save to Photos" : "Download"}
                  className="flex-1 h-14 flex-col gap-0.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 relative"
                >
                  <Download className="h-5 w-5" />
                  <span className="text-[11px] font-medium leading-none flex items-center gap-0.5">
                    {getPlatform() === "ios" ? "Save to Photos" : "Download"}
                    <ChevronUp className="h-3 w-3" />
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="top" className="w-56">
                <DropdownMenuItem onClick={handleDownloadCurrent}>
                  <Download className="h-4 w-4 mr-2" />
                  Download current
                </DropdownMenuItem>
                {transformedImages.length > 1 && (
                  <DropdownMenuItem onClick={handleDownloadAll}>
                    <Download className="h-4 w-4 mr-2" />
                    Download all ({transformedImages.length})
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              onClick={async () => {
                if (!lastSavedDesignId) {
                  toast.error("Please wait — saving your design first");
                  return;
                }
                if (isShared) {
                  const { error } = await supabase
                    .from("saved_designs")
                    .update({ is_public: false, designer_note: null, public_variations: [] })
                    .eq("id", lastSavedDesignId);
                  if (error) {
                    toast.error("Failed to unshare");
                    return;
                  }
                  setIsShared(false);
                  toast.success("Design removed from Explore");
                } else {
                  setShowShareDialog(true);
                }
              }}
              disabled={!lastSavedDesignId || isGenerating}
              aria-label={isShared ? "Shared to Explore" : "Share to Explore"}
              variant="outline"
              className={cn(
                "flex-1 h-14 flex-col gap-0.5 rounded-xl",
                isShared && "bg-green-600 hover:bg-green-700 text-white border-green-600"
              )}
            >
              <Globe className="h-5 w-5" />
              <span className="text-[11px] font-medium leading-none">
                {isShared ? "Shared" : "Share"}
              </span>
            </Button>
            <Button
              onClick={() => navigate(`/feedback-and-testimonials?source=mobile_results${lastSavedDesignId ? `&designId=${lastSavedDesignId}` : ""}`)}
              aria-label="Feedback"
              variant="outline"
              className="flex-1 h-14 flex-col gap-0.5 rounded-xl"
            >
              <MessageSquareHeart className="h-5 w-5" />
              <span className="text-[11px] font-medium leading-none">Feedback</span>
            </Button>
          </div>
        </div>
      )}


      {/* Share to Explore Dialog */}
      <ShareToExploreDialog
        open={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        variations={transformedImages}
        onShare={async (note, selectedUrls) => {
          if (!lastSavedDesignId) return;
          if (selectedUrls.length === 0) {
            toast.error("Select at least one variation to share");
            return;
          }
          const { error } = await supabase
            .from("saved_designs")
            .update({
              is_public: true,
              designer_note: note || null,
              public_variations: selectedUrls,
            })
            .eq("id", lastSavedDesignId);
          if (error) {
            toast.error("Failed to share design");
            return;
          }
          setIsShared(true);
          toast.success(
            selectedUrls.length === 1
              ? "Design shared to Explore! 🎉"
              : `${selectedUrls.length} variations shared to Explore! 🎉`,
          );
        }}
      />

      {/* Upgrade-to-Pro prompt when the server blocks a Pro style mid-generation. */}
      <UpgradeProDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        styleName={lockedStyleName}
      />

      <FullscreenImageViewer
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        images={transformedImages}
        initialIndex={currentVariation}
      />
    </div>
  );
};

export default MobileResults;